import QRCode from "qrcode";

// ─── Binary STL Writer ────────────────────────────────
function createSTL(triangles) {
  const numTriangles = triangles.length;
  const bufferSize = 80 + 4 + numTriangles * 50;
  const buffer = new ArrayBuffer(bufferSize);
  const view = new DataView(buffer);
  const header = "NYC Manufacturing QR Code STL";
  for (let i = 0; i < 80; i++) view.setUint8(i, i < header.length ? header.charCodeAt(i) : 0);
  view.setUint32(80, numTriangles, true);
  let offset = 84;
  for (const tri of triangles) {
    view.setFloat32(offset, tri.n[0], true); offset += 4;
    view.setFloat32(offset, tri.n[1], true); offset += 4;
    view.setFloat32(offset, tri.n[2], true); offset += 4;
    for (const v of tri.v) {
      view.setFloat32(offset, v[0], true); offset += 4;
      view.setFloat32(offset, v[1], true); offset += 4;
      view.setFloat32(offset, v[2], true); offset += 4;
    }
    view.setUint16(offset, 0, true); offset += 2;
  }
  return new Blob([buffer], { type: "application/octet-stream" });
}

// ─── Add a box with correct winding order ─────────────
// All faces use counter-clockwise winding when viewed from outside
function addBox(tris, x, y, z, w, h, d) {
  const x1 = x + w, y1 = y + h, z1 = z + d;

  // Bottom face (z = z, normal -Z)
  tris.push({ n: [0, 0, -1], v: [[x, y, z], [x1, y1, z], [x1, y, z]] });
  tris.push({ n: [0, 0, -1], v: [[x, y, z], [x, y1, z], [x1, y1, z]] });

  // Top face (z = z1, normal +Z)
  tris.push({ n: [0, 0, 1], v: [[x, y, z1], [x1, y, z1], [x1, y1, z1]] });
  tris.push({ n: [0, 0, 1], v: [[x, y, z1], [x1, y1, z1], [x, y1, z1]] });

  // Front face (y = y, normal -Y)
  tris.push({ n: [0, -1, 0], v: [[x, y, z], [x1, y, z], [x1, y, z1]] });
  tris.push({ n: [0, -1, 0], v: [[x, y, z], [x1, y, z1], [x, y, z1]] });

  // Back face (y = y1, normal +Y)
  tris.push({ n: [0, 1, 0], v: [[x, y1, z], [x1, y1, z1], [x1, y1, z]] });
  tris.push({ n: [0, 1, 0], v: [[x, y1, z], [x, y1, z1], [x1, y1, z1]] });

  // Left face (x = x, normal -X)
  tris.push({ n: [-1, 0, 0], v: [[x, y, z], [x, y, z1], [x, y1, z1]] });
  tris.push({ n: [-1, 0, 0], v: [[x, y, z], [x, y1, z1], [x, y1, z]] });

  // Right face (x = x1, normal +X)
  tris.push({ n: [1, 0, 0], v: [[x1, y, z], [x1, y1, z1], [x1, y, z1]] });
  tris.push({ n: [1, 0, 0], v: [[x1, y, z], [x1, y1, z], [x1, y1, z1]] });
}

// ─── Generate STL ─────────────────────────────────────
export function generateQRCodeSTL(text, labelText, options = {}) {
  const {
    moduleSize = 1,
    baseHeight = 2,
    qrHeight = 3.5,
    basePadding = 2,
    textPosition = "below", // "below", "above", "left", "right"
    textScale = 1, // 0.25 to 2, scales the STL text size
  } = options;

  let qr;
  try {
    qr = QRCode.create(text, { errorCorrectionLevel: "M" });
  } catch (e) {
    return Promise.resolve(null);
  }

  const modules = qr.modules;
  const qrSize = modules.size;
  const qrMm = qrSize * moduleSize;
  const extrudeH = qrHeight - baseHeight;

  // Render label text to pixel grid, then mirror Y for STL
  let textRects = [];
  let textHeightMm = 0;
  if (labelText) {
    const pixPerMm = 16;
    const canvasW = Math.ceil(qrMm * pixPerMm);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const fSize = Math.max(6, Math.round(qrMm * 0.55 * pixPerMm * textScale));
    ctx.font = `bold ${fSize}px Arial`;

    const lines = [];
    labelText.split("\n").forEach((raw) => {
      const words = raw.split(" ");
      let cur = "";
      words.forEach((w) => {
        const test = cur ? cur + " " + w : w;
        if (ctx.measureText(test).width > canvasW && cur) { lines.push(cur); cur = w; }
        else cur = test;
      });
      if (cur) lines.push(cur);
    });

    const lineH = fSize + 2;
    const canvasH = lines.length * lineH + 4;
    canvas.width = canvasW;
    canvas.height = canvasH;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvasW, canvasH);
    ctx.fillStyle = "#000000";
    ctx.font = `bold ${fSize}px Arial`;
    ctx.textAlign = "center";
    lines.forEach((line, i) => ctx.fillText(line, canvasW / 2, (i + 1) * lineH));

    const imgData = ctx.getImageData(0, 0, canvasW, canvasH);
    const pxSize = 1 / pixPerMm;
    textHeightMm = canvasH * pxSize;

    // Merge horizontal runs, mirror Y so text reads correctly in STL
    for (let py = 0; py < canvasH; py++) {
      const mirroredY = (canvasH - 1 - py) * pxSize;
      let runStart = -1;
      for (let px = 0; px <= canvasW; px++) {
        const dark = px < canvasW && imgData.data[(py * canvasW + px) * 4] < 128;
        if (dark && runStart < 0) runStart = px;
        else if (!dark && runStart >= 0) {
          textRects.push({ x: runStart * pxSize, y: mirroredY, w: (px - runStart) * pxSize, h: pxSize });
          runStart = -1;
        }
      }
    }
  }

  // Calculate text width for left/right positioning
  const textWidthMm = textRects.length > 0 ? Math.max(...textRects.map((r) => r.x + r.w)) : 0;
  const gap = 1; // mm gap between QR and text

  // Calculate plate size and offsets based on text position
  let plateW, plateD, qrOffX, qrOffY, textOffX, textOffY;

  if (textPosition === "below") {
    plateW = qrMm + basePadding * 2;
    plateD = qrMm + (textHeightMm > 0 ? textHeightMm + gap : 0) + basePadding * 2;
    qrOffX = basePadding;
    qrOffY = basePadding + (textHeightMm > 0 ? textHeightMm + gap : 0);
    textOffX = basePadding;
    textOffY = basePadding;
  } else if (textPosition === "above") {
    plateW = qrMm + basePadding * 2;
    plateD = qrMm + (textHeightMm > 0 ? textHeightMm + gap : 0) + basePadding * 2;
    qrOffX = basePadding;
    qrOffY = basePadding;
    textOffX = basePadding;
    textOffY = basePadding + qrMm + gap;
  } else if (textPosition === "left") {
    plateW = qrMm + (textWidthMm > 0 ? textWidthMm + gap : 0) + basePadding * 2;
    plateD = Math.max(qrMm, textHeightMm) + basePadding * 2;
    qrOffX = basePadding + (textWidthMm > 0 ? textWidthMm + gap : 0);
    qrOffY = basePadding;
    textOffX = basePadding;
    textOffY = basePadding + Math.max(0, (qrMm - textHeightMm) / 2);
  } else { // right
    plateW = qrMm + (textWidthMm > 0 ? textWidthMm + gap : 0) + basePadding * 2;
    plateD = Math.max(qrMm, textHeightMm) + basePadding * 2;
    qrOffX = basePadding;
    qrOffY = basePadding;
    textOffX = basePadding + qrMm + gap;
    textOffY = basePadding + Math.max(0, (qrMm - textHeightMm) / 2);
  }

  const tris = [];

  // Base plate
  addBox(tris, 0, 0, 0, plateW, plateD, baseHeight);

  // QR modules
  for (let row = 0; row < qrSize; row++) {
    for (let col = 0; col < qrSize; col++) {
      if (modules.get(col, row)) {
        addBox(tris,
          qrOffX + col * moduleSize,
          qrOffY + row * moduleSize,
          baseHeight,
          moduleSize, moduleSize, extrudeH
        );
      }
    }
  }

  // Text
  if (textRects.length > 0) {
    for (const r of textRects) {
      addBox(tris, textOffX + r.x, textOffY + r.y, baseHeight, r.w, r.h, extrudeH);
    }
  }

  return Promise.resolve({ blob: createSTL(tris), triangles: tris });
}
